import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`PostHog is available for install via `}<a parentName="p" {...{
        "href": "https://pub.dev/packages/posthog_flutter"
      }}>{`Pub`}</a>{`.`}</p>
    <h3 {...{
      "id": "configuration"
    }}>{`Configuration`}</h3>
    <p>{`Set your PostHog API key and change the automatic event tracking on if you wish the library to take care of it for you.`}</p>
    <p>{`Remember that the application lifecycle events won't have any special context set for you by the time it is initialized. If you are using a self-hosted instance of PostHog you will need to have the public hostname or IP for your instance as well.`}</p>
    <p>{`To start, add `}<inlineCode parentName="p">{`posthog_flutter`}</inlineCode>{` to your `}<inlineCode parentName="p">{`pubspec.yaml`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml",
        "metastring": "file=pubspec.yaml",
        "file": "pubspec.yaml"
      }}>{`# rest of your code

dependencies:
  flutter:
    sdk: flutter
  posthog_flutter: ^4.0.1

# rest of your code
`}</code></pre>
    <p>{`Then complete the set up for each platform:`}</p>
    <h4 {...{
      "id": "android-setup"
    }}>{`Android setup`}</h4>
    <p>{`For Android, add your PostHog configuration to your `}<inlineCode parentName="p">{`AndroidManifest.xml`}</inlineCode>{` file located in the `}<inlineCode parentName="p">{`android/app/src/main`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml",
        "metastring": "file=android/app/src/main/AndroidManifest.xml",
        "file": "android/app/src/main/AndroidManifest.xml"
      }}>{`<manifest xmlns:android="http://schemas.android.com/apk/res/android" package="your.package.name">
    <application>
        <!-- ... other configuration ... -->
        <meta-data android:name="com.posthog.posthog.API_KEY" android:value="<ph_project_api_key>" />
        <meta-data android:name="com.posthog.posthog.POSTHOG_HOST" android:value="<ph_instance_address>" />  <!-- usually 'https://app.posthog.com' or 'https://eu.posthog.com' -->
        <meta-data android:name="com.posthog.posthog.TRACK_APPLICATION_LIFECYCLE_EVENTS" android:value="true" />
        <meta-data android:name="com.posthog.posthog.DEBUG" android:value="true" />
    </application>
</manifest>
`}</code></pre>
    <p>{`You'll also need to update the minimum Android SDK version to `}<inlineCode parentName="p">{`21`}</inlineCode>{` in `}<inlineCode parentName="p">{`android/app/build.gradle`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-gradle_kotlin",
        "metastring": "file=android/app/build.gradle",
        "file": "android/app/build.gradle"
      }}>{`// rest of your config

    defaultConfig {
      minSdkVersion 21
      // rest of your config
    }

// rest of your config
`}</code></pre>
    <h4 {...{
      "id": "ios-setup"
    }}>{`iOS setup`}</h4>
    <p>{`For iOS, you'll need to have `}<a parentName="p" {...{
        "href": "https://guides.cocoapods.org/using/getting-started.html"
      }}>{`Cocoapods`}</a>{` installed. Then add your PostHog configuration to the `}<inlineCode parentName="p">{`Info.plist`}</inlineCode>{` file located in the `}<inlineCode parentName="p">{`ios/Runner`}</inlineCode>{` directory:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml",
        "metastring": "file=ios/Runner/Info.plist",
        "file": "ios/Runner/Info.plist"
      }}>{`<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
<!-- rest of your configuration -->
  <key>com.posthog.posthog.API_KEY</key>
  <string><ph_project_api_key></string>
  <key>com.posthog.posthog.POSTHOG_HOST</key>
  <string><ph_instance_address></string>
  <key>com.posthog.posthog.CAPTURE_APPLICATION_LIFECYCLE_EVENTS</key>
  <true/>
  <key>com.posthog.posthog.DEBUG</key>
  <true/>
</dict>
</plist>
`}</code></pre>
    <p>{`Then you need to set the minimum platform version to iOS 13.0 in your Podfile:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml",
        "metastring": "file=ios/Podfile",
        "file": "ios/Podfile"
      }}>{`platform :ios, '13.0'

# rest of your config
`}</code></pre>
    <h4 {...{
      "id": "web-setup"
    }}>{`Web setup`}</h4>
    <p>{`For Web, add your `}<inlineCode parentName="p">{`Web snippet`}</inlineCode>{` (which you can find in `}<a parentName="p" {...{
        "href": "https://us.posthog.com/settings/project#snippet"
      }}>{`your project settings`}</a>{`) in the `}<inlineCode parentName="p">{`<header>`}</inlineCode>{` of your `}<inlineCode parentName="p">{`web/index.html`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "file=web/index.html",
        "file": "web/index.html"
      }}>{`<!DOCTYPE html>
<html>

<head>
  <!-- ... other head elements ... -->

  <script async>
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    posthog.init(
      '<ph_project_api_key>',
      {
        api_host:'<ph_instance_address>', // 'https://app.posthog.com' or 'https://eu.posthog.com'
      }
    )
  </script>
</head>

<!-- other elements -->

</html>

`}</code></pre>
    <p>{`For more information please check: `}<a parentName="p" {...{
        "href": "https://posthog.com/docs/libraries/js"
      }}>{`https://posthog.com/docs/libraries/js`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      